/**
 * Landing — what a buyer saves without a buyer's agent, per loan program.
 *
 * Each bar is the example purchase price split into the program's typical
 * down payment and the loan, extended by the buyer's agent commission the
 * buyer doesn't pay. Cash comes first as the base case. Down payments come
 * from defaultDownPaymentPercent, the same starting point property pages use.
 */

import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { defaultDownPaymentPercent } from '@/utils/propertyDownPayment';

export const EXAMPLE_PRICE = 300_000;
/** Buyer's agent commission the buyer doesn't pay, percent of price. */
export const AGENT_COMMISSION_PERCENT = 3;
export const AGENT_SAVINGS = (EXAMPLE_PRICE * AGENT_COMMISSION_PERCENT) / 100;

const DOWN_COLOR = '#2a78d6';
const LOAN_COLOR = '#c7dcf5';
const SAVINGS_COLOR = '#1f8a5b';
const GRID_COLOR = '#e1e0d9';
const ROW_HEIGHT = 42;

/** Purchase programs, in display order; Cash is the base case. */
const PURCHASE_PROGRAMS: { key: string; program: USER_LOAN_PROGRAM }[] = [
  { key: 'cash', program: USER_LOAN_PROGRAM.CASH },
  { key: 'conventional', program: USER_LOAN_PROGRAM.CONVENTIONAL },
  { key: 'fha', program: USER_LOAN_PROGRAM.FHA },
  { key: 'va', program: USER_LOAN_PROGRAM.VA },
  { key: 'usda', program: USER_LOAN_PROGRAM.USDA },
  { key: 'dscr', program: USER_LOAN_PROGRAM.DSCR },
  { key: 'commercial', program: USER_LOAN_PROGRAM.COMMERCIAL },
  { key: 'fixAndFlip', program: USER_LOAN_PROGRAM.FIX_AND_FLIP },
  { key: 'bridge', program: USER_LOAN_PROGRAM.BRIDGE },
];

const usd = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

interface SavingsRow {
  key: string;
  label: string;
  downPct: number;
  down: number;
  loan: number;
  savings: number;
  tipLabel: string;
}

function savingsLabel(down: number, isCash: boolean): string {
  if (isCash) return `Save ${usd(AGENT_SAVINGS)} on a cash purchase`;
  if (down <= 0) return `$0 down · save ${usd(AGENT_SAVINGS)}`;
  const share = Math.round((AGENT_SAVINGS / down) * 100);
  return `${usd(down)} down · save ${usd(AGENT_SAVINGS)} (${share}% of it)`;
}

/** Rows for the programs the rate config publishes (plus Cash). */
export function agentSavingsRows(
  labels: Record<string, string | undefined>
): SavingsRow[] {
  return PURCHASE_PROGRAMS.filter(
    ({ key }) => key === 'cash' || Boolean(labels[key])
  ).map(({ key, program }) => {
    const downPct = defaultDownPaymentPercent(program);
    const down = Math.round(EXAMPLE_PRICE * downPct);
    return {
      key,
      label: key === 'cash' ? 'Cash' : (labels[key] as string),
      downPct,
      down,
      loan: EXAMPLE_PRICE - down,
      savings: AGENT_SAVINGS,
      tipLabel: savingsLabel(down, key === 'cash'),
    };
  });
}

const SavingsTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: SavingsRow }[];
}) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const line = (color: string, name: string, value: string) => (
    <Stack direction="row" alignItems="center" gap={1}>
      <Box
        sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {name}
      </Typography>
      <Typography
        variant="body2"
        sx={{ ml: 2, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
    </Stack>
  );
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        boxShadow: 3,
        minWidth: 260,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {row.label} · {usd(EXAMPLE_PRICE)} home
      </Typography>
      {line(
        DOWN_COLOR,
        row.key === 'cash'
          ? 'Paid in cash'
          : `Typical down payment (${(row.downPct * 100)
              .toFixed(1)
              .replace(/\.0$/, '')}%)`,
        usd(row.down)
      )}
      {row.loan > 0 && line(LOAN_COLOR, 'Loan amount', usd(row.loan))}
      {line(SAVINGS_COLOR, "Saved without a buyer's agent", usd(row.savings))}
    </Box>
  );
};

export const LandingAgentSavingsChart = ({
  labels,
}: {
  labels: Record<string, string | undefined>;
}) => {
  const theme = useTheme();
  const textMuted = theme.palette.text.secondary;
  const rows = agentSavingsRows(labels);
  const domainMax = EXAMPLE_PRICE + AGENT_SAVINGS * 2;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        What you save without a buyer&apos;s agent
      </Typography>
      <Typography variant="body2" sx={{ color: textMuted, mb: 1.5 }}>
        A {usd(EXAMPLE_PRICE)} home with each program&apos;s typical down
        payment. The green extension is the {AGENT_COMMISSION_PERCENT}%
        commission you keep — take it off the price or off your cash to close.
      </Typography>

      <Stack
        direction="row"
        gap={3}
        flexWrap="wrap"
        sx={{ mb: 2, pl: { sm: '150px' } }}
      >
        {[
          { color: DOWN_COLOR, name: 'Down payment (cash)' },
          { color: LOAN_COLOR, name: 'Loan amount' },
          { color: SAVINGS_COLOR, name: "Saved without a buyer's agent" },
        ].map(({ color, name }) => (
          <Stack key={name} direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '3px',
                bgcolor: color,
              }}
            />
            <Typography variant="caption" sx={{ color: textMuted }}>
              {name}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box
        role="img"
        aria-label={`Savings without a buyer's agent on a ${usd(
          EXAMPLE_PRICE
        )} home: ${usd(AGENT_SAVINGS)} for every program. ${rows
          .map((r) => `${r.label}: ${r.tipLabel}`)
          .join('. ')}.`}
        sx={{ height: rows.length * ROW_HEIGHT + 40 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 0, right: 230, bottom: 0, left: 0 }}
            barSize={22}
          >
            <CartesianGrid
              horizontal={false}
              stroke={GRID_COLOR}
              strokeWidth={1}
            />
            <XAxis
              type="number"
              domain={[0, domainMax]}
              ticks={[0, 100_000, 200_000, EXAMPLE_PRICE]}
              tickFormatter={(v: number) => `$${Math.round(v / 1000)}k`}
              axisLine={false}
              tickLine={false}
              tick={{ fill: textMuted, fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={150}
              axisLine={{ stroke: GRID_COLOR }}
              tickLine={false}
              tick={{ fill: theme.palette.text.primary, fontSize: 13 }}
            />
            <RechartTooltip
              content={<SavingsTooltip />}
              cursor={{ fill: theme.palette.action.hover }}
            />
            <Bar
              dataKey="down"
              stackId="price"
              fill={DOWN_COLOR}
              stroke="#ffffff"
              strokeWidth={1}
              animationDuration={900}
            />
            <Bar
              dataKey="loan"
              stackId="price"
              fill={LOAN_COLOR}
              stroke="#ffffff"
              strokeWidth={1}
              animationDuration={900}
            />
            <Bar
              dataKey="savings"
              stackId="price"
              fill={SAVINGS_COLOR}
              stroke="#ffffff"
              strokeWidth={1}
              radius={[0, 4, 4, 0]}
              animationDuration={900}
            >
              <LabelList
                dataKey="tipLabel"
                position="right"
                style={{
                  fill: theme.palette.text.primary,
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        <table>
          <caption>
            Savings without a buyer&apos;s agent on a {usd(EXAMPLE_PRICE)} home
          </caption>
          <thead>
            <tr>
              <th scope="col">Program</th>
              <th scope="col">Typical down payment</th>
              <th scope="col">Loan amount</th>
              <th scope="col">Saved without a buyer&apos;s agent</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <th scope="row">{r.label}</th>
                <td>{usd(r.down)}</td>
                <td>{usd(r.loan)}</td>
                <td>{usd(r.savings)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};
